import { useCallback, useState } from 'react';

import { useNotifications } from '@decisiv/ui-components';

import useAccountSearch from '../../shared/useAccountSearch';
import {
  addAccountAccess,
  fetchAssignableAccounts,
} from '../services/accountAccessService';

const NOTIFICATION_DURATION = 8000;

export const ADDED_MESSAGE = 'Account access added.';
const GENERIC_ERROR = 'Something went wrong. Please try again.';

const errorMessage = (errors) => (errors.base || []).join(' ') || GENERIC_ERROR;

const useAddToAccount = (userId, { onAdded } = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  const fetchPage = useCallback(
    (query, offset) => fetchAssignableAccounts(userId, query, offset),
    [userId],
  );

  const {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    searchAccounts,
  } = useAccountSearch(fetchPage, isOpen);

  const { notify } = useNotifications();

  const open = useCallback(() => {
    setIsOpen(true);
    setSelectedAccountIds([]);
    setModalError(null);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSelectedAccountIds([]);
    setModalError(null);
  }, []);

  const submit = useCallback(async () => {
    if (selectedAccountIds.length === 0) return;

    setIsSubmitting(true);
    setModalError(null);

    try {
      const result = await addAccountAccess(userId, selectedAccountIds);

      if (result.errors) {
        setModalError(errorMessage(result.errors));

        return;
      }

      if (onAdded) onAdded(result.accountAccess);

      notify({
        intent: 'success',
        title: ADDED_MESSAGE,
        duration: NOTIFICATION_DURATION,
        onClose: () => {},
      });
      close();
    } catch (error) {
      setModalError(GENERIC_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, [close, notify, onAdded, selectedAccountIds, userId]);

  return {
    accountOptions,
    accountsTotal,
    close,
    hasMoreAccounts,
    isLoadingAccounts,
    isOpen,
    isSubmitting,
    loadMoreAccounts,
    modalError,
    open,
    searchAccounts,
    selectedAccountIds,
    setSelectedAccountIds,
    submit,
  };
};

export default useAddToAccount;
